import React, { useEffect } from 'react';
import { CYCLE_YEAR } from '../../config/recruitingCycle';
import axios from '../../utils/axiosConfig';
import ExternalLink from '../common/ExternalLink';
// UserInfo Component
export const UserInfo = ({
  name,
  school,
  joinDate,
  profilePictureUrl,
  questPlan,
  onAcceptQuest,
}) => (
  <div className="text-center">
    <img
      src="/kitty_laugh.png"
      alt={`${name}'s profile`}
      className="w-24 h-24 rounded-[20%] border-4 border-white shadow-xl mx-auto mb-4 bg-linear-to-br from-blue-50 to-white"
    />
    <h2 className="text-xl font-semibold">{name}</h2>
    <p className="text-gray-600">{school}</p>
    <p className="text-gray-500">Joined on {joinDate}</p>
    {questPlan && questPlan.quest_plan && questPlan.quest_plan.length > 0 ? (
      <div className="mt-2 px-4 py-1 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-xs relative overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-r from-purple-600 to-blue-400 animate-pulse-gradient"></div>
        <div className="absolute inset-0 bg-linear-to-r from-pink-500 to-indigo-500 animate-pulse-gradient-reverse opacity-70"></div>
        {/* No year here -- this also renders for users still on a previous cycle's quest. */}
        <span className="relative z-10">🗺️ Quest Accepted</span>
      </div>
    ) : (
      <button
        onClick={onAcceptQuest}
        className="mt-2 px-4 py-1 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-xs relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-linear-to-r from-purple-600 to-blue-400 animate-pulse-gradient"></div>
        <div className="absolute inset-0 bg-linear-to-r from-pink-500 to-indigo-500 animate-pulse-gradient-reverse opacity-70"></div>
        <span className="relative z-10">Accept the {CYCLE_YEAR} InternHub Quest →</span>
      </button>
    )}
  </div>
);

// Calendar Component with Week/Month View and Streak Progress
export const Calendar = ({ activeDays, checkAchievements, userID }) => {
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // State management
  const [currentStreak, setCurrentStreak] = React.useState(0);
  const [viewMode, setViewMode] = React.useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = React.useState(new Date());

  // Helper functions
  const getWeekDates = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day); // Go to Sunday

    for (let i = 0; i < 7; i++) {
      const weekDate = new Date(startOfWeek);
      weekDate.setDate(startOfWeek.getDate() + i);
      week.push(weekDate);
    }
    return week;
  };

  const formatDateString = (date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const isDayActive = (date) => {
    const dateString = formatDateString(date);
    return activeDays.includes(dateString);
  };

  const getDayStatus = (date) => {
    const today = new Date();
    const dateString = formatDateString(date);
    const isActive = activeDays.includes(dateString);
    const isToday = date.toDateString() === today.toDateString();
    const isPast = date < today && !isToday;
    const isFuture = date > today;

    if (isToday) {
      return {
        type: 'today',
        isActive,
        label: isActive ? 'Completed Today!' : 'In Progress',
        bgClass: isActive
          ? 'bg-linear-to-br from-orange-400 to-red-400 text-white shadow-lg'
          : 'bg-linear-to-br from-blue-400 to-blue-500 text-white shadow-lg ring-2 ring-blue-300 animate-pulse',
        icon: isActive ? '✓' : '⚡',
      };
    }

    if (isFuture) {
      return {
        type: 'future',
        isActive: false,
        label: 'Upcoming',
        bgClass: 'bg-gray-100 border-2 border-gray-200 text-gray-400',
        icon: null,
      };
    }

    if (isPast) {
      if (isActive) {
        return {
          type: 'completed',
          isActive: true,
          label: 'Completed',
          bgClass: 'bg-linear-to-br from-orange-400 to-red-400 text-white shadow-md',
          icon: '✓',
        };
      } else {
        return {
          type: 'skipped',
          isActive: false,
          label: 'Skipped',
          bgClass: 'bg-linear-to-br from-red-100 to-red-200 border-2 border-red-300 text-red-600',
          icon: '✗',
        };
      }
    }

    return {
      type: 'normal',
      isActive,
      label: 'Normal',
      bgClass: 'bg-white border-2 border-gray-200 text-gray-700',
      icon: null,
    };
  };

  const isPartOfStreak = (date, weekDates) => {
    const dateIndex = weekDates.findIndex((d) => d.toDateString() === date.toDateString());
    if (dateIndex === -1) return false;

    // Check if this day and the previous day are both active
    const currentActive = isDayActive(date);
    if (!currentActive) return false;

    if (dateIndex > 0) {
      const previousDay = weekDates[dateIndex - 1];
      const previousActive = isDayActive(previousDay);
      if (previousActive) return true;
    }

    if (dateIndex < weekDates.length - 1) {
      const nextDay = weekDates[dateIndex + 1];
      const nextActive = isDayActive(nextDay);
      if (nextActive) return true;
    }

    return false;
  };

  // Fetch streak from backend
  const fetchStreak = async () => {
    try {
      const response = await axios.get(`/api/v1/user/${userID}/streak`);
      setCurrentStreak(response.data.streak);
    } catch (error) {
      console.error('Error fetching streak:', error.response?.data?.detail || error.message);
      setCurrentStreak(0);
    }
  };

  // Navigation functions
  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction * 7);
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Effects
  React.useEffect(() => {
    if (userID) {
      fetchStreak();
    }
  }, [userID]);

  // Check for streak achievement
  useEffect(() => {
    if (currentStreak >= 2) {
      checkAchievements.checkStreak(currentStreak);
    }
  }, [currentStreak]);

  // Render functions
  const renderStreakProgress = () => {
    const streakMilestones = [3, 7, 14, 30, 60, 100];
    const nextMilestone = streakMilestones.find((milestone) => milestone > currentStreak) || 100;
    const progress = Math.min((currentStreak / nextMilestone) * 100, 100);

    return (
      <div className="mb-6 p-4 bg-linear-to-r from-orange-50 to-red-50 rounded-xl border border-orange-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🔥</span>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {currentStreak === 0 ? 'Start your streak!' : `${currentStreak} day streak`}
              </p>
              <p className="text-xs text-gray-600">
                {currentStreak === 0
                  ? 'Complete your first task'
                  : `${nextMilestone - currentStreak} days to ${nextMilestone}`}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Next milestone</div>
            <div className="text-sm font-bold text-orange-600">{nextMilestone} days</div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-linear-to-r from-orange-400 to-red-400 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);
    const today = new Date();

    return (
      <div className="flex flex-col space-y-4 p-4">
        {/* Week Navigation */}
        <div className="flex items-center justify-between shrink-0 px-2">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="text-center">
            <h3 className="text-base font-semibold text-gray-800">This Week</h3>
            <p className="text-xs text-gray-600">
              {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
              {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          </div>

          <button
            onClick={() => navigateWeek(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7 gap-3 px-2">
          {weekDates.map((date, index) => {
            const dayName = weekdays[index];
            const dayStatus = getDayStatus(date);
            const partOfStreak = isPartOfStreak(date, weekDates);

            return (
              <div key={index} className="text-center flex flex-col min-h-[100px]">
                <div className="text-xs font-medium text-gray-600 mb-2">{dayName}</div>
                <div className="relative flex-1 flex items-center justify-center py-2">
                  {/* Streak connector line - show if current day is completed and next day is also completed */}
                  {dayStatus.isActive &&
                    index < 6 &&
                    (() => {
                      const nextDate = weekDates[index + 1];
                      const nextDayStatus = getDayStatus(nextDate);
                      // Show line if next day is completed (including today if completed)
                      return nextDayStatus.isActive ? (
                        <div className="absolute top-1/2 right-0 w-10 h-2 bg-linear-to-r from-orange-400 to-red-400 transform translate-x-6 -translate-y-1/2 z-0 rounded-full shadow-xs"></div>
                      ) : null;
                    })()}

                  <div
                    className={`
                                            relative w-12 h-12 rounded-xl flex items-center justify-center text-sm font-medium
                                            transition-all duration-300 hover:scale-105 z-10
                                            ${dayStatus.bgClass}
                                        `}
                    title={dayStatus.label}
                  >
                    {date.getDate()}

                    {/* Status icon overlay - show fire emoji for streak days, otherwise show day status icon */}
                    {partOfStreak && dayStatus.isActive ? (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-xs border border-gray-200">
                        <span className="text-xs">🔥</span>
                      </div>
                    ) : dayStatus.icon ? (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-xs border border-gray-200">
                        <span className="text-xs">{dayStatus.icon}</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Day status label */}
                <div
                  className={`text-xs mt-1 font-medium ${
                    dayStatus.type === 'today'
                      ? 'text-blue-600'
                      : dayStatus.type === 'completed'
                        ? 'text-green-600'
                        : dayStatus.type === 'skipped'
                          ? 'text-red-500'
                          : 'text-gray-400'
                  }`}
                >
                  {dayStatus.type === 'today'
                    ? dayStatus.isActive
                      ? 'Done!'
                      : 'Today'
                    : dayStatus.type === 'completed'
                      ? 'Done'
                      : dayStatus.type === 'skipped'
                        ? 'Missed'
                        : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startingDayIndex = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    const today = new Date();

    return (
      <div className="space-y-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <h3 className="text-lg font-semibold text-gray-800">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>

          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Month Grid */}
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-2">
            {weekdays.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for padding */}
            {[...Array(startingDayIndex)].map((_, index) => (
              <div key={`empty-${index}`} className="h-10" />
            ))}

            {/* Date cells */}
            {[...Array(daysInMonth)].map((_, i) => {
              const date = i + 1;
              const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
              const isToday =
                date === today.getDate() &&
                currentDate.getMonth() === today.getMonth() &&
                currentDate.getFullYear() === today.getFullYear();
              const isActive = isDayActive(cellDate);

              return (
                <div key={i} className="h-10 flex items-center justify-center">
                  <div
                    className={`
                                            w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium
                                            transition-all duration-200
                                            ${
                                              isActive
                                                ? 'bg-orange-500 text-white shadow-xs'
                                                : 'text-gray-700 hover:bg-gray-100'
                                            }
                                            ${isToday ? 'ring-2 ring-blue-400' : ''}
                                        `}
                  >
                    {date}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Streak Progress */}
      {renderStreakProgress()}

      {/* View Toggle */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'week'
                ? 'bg-white text-gray-900 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'month'
                ? 'bg-white text-gray-900 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Month
          </button>
        </div>

        <button
          onClick={goToToday}
          className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          Today
        </button>
      </div>

      {/* Calendar View */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'week' ? renderWeekView() : renderMonthView()}
      </div>
    </div>
  );
};

// TaskList Component
export const TaskList = ({ tasks, onTaskClick }) => (
  <ul className="space-y-4">
    {tasks.map((task) => (
      <li key={task.id} className="flex items-center">
        <input
          type="checkbox"
          className="form-checkbox mr-2"
          checked={task.completed}
          onChange={() => onTaskClick(task.id)}
        />
        <span>{task.title}</span>
      </li>
    ))}
  </ul>
);

// UpcomingEvents Component
export const UpcomingEvents = ({ events }) => (
  <ul className="space-y-4">
    {events.map((event) => (
      <li key={event.id} className="flex items-center">
        <ExternalLink href={event.url} className="text-blue-500 hover:underline grow">
          {event.title}
        </ExternalLink>
        <span className="text-gray-500 w-32 shrink-0 ml-4">{event.date}</span>
      </li>
    ))}
  </ul>
);

// Badges Component
export const Badges = ({ badges }) => (
  <div className="grid grid-cols-4 gap-4">
    {badges.map((badge) => (
      <ExternalLink href={badge.url} key={badge.id}>
        <img
          src={badge.imageUrl}
          alt={`Badge ${badge.id}`}
          className="w-12 h-12 object-cover rounded-full mx-auto"
        />
      </ExternalLink>
    ))}
  </div>
);

<style jsx>{`
  @keyframes pulseGradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
  @keyframes pulseGradientReverse {
    0% {
      background-position: 100% 50%;
    }
    50% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 100% 50%;
    }
  }
  .animate-pulse-gradient {
    background-size: 200% 200%;
    animation: pulseGradient 3s ease infinite;
  }
  .animate-pulse-gradient-reverse {
    background-size: 200% 200%;
    animation: pulseGradientReverse 3s ease infinite;
  }
`}</style>;
